// src/components/employee/OnboardingWizardRedesign.js
// UI redesign of the employee onboarding wizard. All state is read from /
// written to the existing OnboardingContext + OnboardingChecklistConfig so
// the functional layer is untouched.
import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import {
    Gift, User, FileText, Landmark, CheckCircle2, Check, Sparkles, Lock,
    ChevronLeft, ChevronRight, Clock, Upload, MoreHorizontal, Mail, X,
} from 'lucide-react';
import { useAuth } from '../../hooks/useAuth';
import { useTheme } from '../../contexts/ThemeContext';
import { useOnboarding } from '../../contexts/OnboardingContext';
import { ONBOARDING_CHECKLIST } from '../../contexts/OnboardingChecklistConfig';
import { palette as buildPalette } from '../admin/onboarding/onboardingTheme';
import uploadService from '../../services/upload.service';
import vibeService from '../../services/vibe.service';

/* Palette context — every primitive in this file reads its colours from here */
const PaletteCtx = createContext(buildPalette(false));
const useP = () => useContext(PaletteCtx);

/* ────────────────────────────────────────────────────────────
   Step definitions (visual). Each maps to one or more
   ONBOARDING_CHECKLIST sections via `sectionKeys`. We never
   touch the checklist schema — only how it is presented.
   ──────────────────────────────────────────────────────────── */
const STEPS = [
    { id: 'welcome',   label: 'Welcome',     icon: Gift,         blurb: 'Get oriented',     sectionKeys: [] },
    { id: 'about',     label: 'About you',   icon: User,         blurb: 'Personal info',    sectionKeys: ['basicInfo'] },
    { id: 'documents', label: 'Documents',   icon: FileText,     blurb: 'Upload files',     sectionKeys: ['identityDocuments', 'educationalDocuments', 'backgroundVerification', 'otherInformation'] },
    { id: 'banking',   label: 'Banking',     icon: Landmark,     blurb: 'Pay setup',        sectionKeys: ['payrollBanking'] },
    { id: 'review',    label: 'Review',      icon: CheckCircle2, blurb: 'Submit for review', sectionKeys: ['hrLegalForms', 'itSystemAccess'] },
];

/* ────────────────────────────────────────────────────────────
   Theme-aware UI primitives — read from a passed-in `p` palette.
   ──────────────────────────────────────────────────────────── */
const Card = ({ children, style = {}, padding = 24, ...rest }) => {
    const p = useP();
    return (
        <div
            {...rest}
            style={{
                background: p.surface,
                borderRadius: 12,
                padding,
                border: `1px solid ${p.border}`,
                boxShadow: p.cardShadow,
                ...style,
            }}
        >
            {children}
        </div>
    );
};

const Pill = ({ children, tone = 'gray' }) => {
    const p = useP();
    const tones = {
        gray:  { bg: p.pillGrayBg,  fg: p.pillGrayFg,  bd: p.pillGrayBd },
        blue:  { bg: p.pillBlueBg,  fg: p.pillBlueFg,  bd: p.pillBlueBd },
        green: { bg: p.pillGreenBg, fg: p.pillGreenFg, bd: p.pillGreenBd },
        amber: { bg: p.pillAmberBg, fg: p.pillAmberFg, bd: p.pillAmberBd },
    };
    const t = tones[tone] || tones.gray;
    return (
        <span style={{
            display: 'inline-flex', alignItems: 'center', gap: 5,
            background: t.bg, color: t.fg, border: `1px solid ${t.bd}`,
            padding: '3px 8px', borderRadius: 999, fontSize: 11.5, fontWeight: 600,
            whiteSpace: 'nowrap',
        }}>{children}</span>
    );
};

const Btn = ({ children, variant = 'primary', leftIcon, rightIcon, style = {}, disabled, ...rest }) => {
    const p = useP();
    const variants = {
        primary:   { bg: p.btnPrimaryBg, fg: p.btnPrimaryFg, bd: p.btnPrimaryBd },
        secondary: { bg: p.btnSecondBg,  fg: p.btnSecondFg,  bd: p.btnSecondBd },
        ghost:     { bg: 'transparent',  fg: p.btnGhostFg,   bd: 'transparent' },
        success:   { bg: p.btnSuccessBg, fg: p.btnSuccessFg, bd: p.btnSuccessBg },
        gold:      { bg: p.btnGoldBg,    fg: p.btnGoldFg,    bd: p.btnGoldBg },
    };
    const v = variants[variant] || variants.primary;
    return (
        <button
            {...rest}
            disabled={disabled}
            style={{
                display: 'inline-flex', alignItems: 'center', gap: 7,
                background: v.bg, color: v.fg, border: `1px solid ${v.bd}`,
                padding: '9px 16px', borderRadius: 8,
                fontSize: 13.5, fontWeight: 600, fontFamily: 'inherit',
                cursor: disabled ? 'not-allowed' : 'pointer',
                opacity: disabled ? 0.55 : 1,
                whiteSpace: 'nowrap',
                transition: 'transform .12s, box-shadow .12s',
                ...style,
            }}
        >
            {leftIcon}{children}{rightIcon}
        </button>
    );
};

/* Field — supports text/email/tel/textarea/date/select/checkbox via item.type */
const Field = ({ item, value, error, onCommit, readonly = false }) => {
    const p = useP();
    const [local, setLocal] = useState(value ?? '');
    useEffect(() => { setLocal(value ?? ''); }, [value]);

    const labelEl = (
        <span style={{ fontSize: 12.5, fontWeight: 600, color: p.textSecondary }}>
            {item.label}
            {item.required && <span style={{ color: '#dc2626' }}> *</span>}
        </span>
    );

    const commit = () => {
        if ((value ?? '') !== local) onCommit(local);
    };

    const base = {
        background: p.inputBg,
        border: `1px solid ${error ? '#fca5a5' : p.inputBorder}`,
        borderRadius: 8,
        padding: '10px 12px',
        fontSize: 14,
        color: p.inputText,
        width: '100%',
        fontFamily: 'inherit',
    };

    if (item.type === 'textarea') {
        return (
            <label style={{ display: 'flex', flexDirection: 'column', gap: 6, gridColumn: 'span 2' }}>
                {labelEl}
                <textarea
                    value={local}
                    onChange={(e) => setLocal(e.target.value)}
                    onBlur={commit}
                    readOnly={readonly || item.readonly}
                    style={{ ...base, minHeight: 64, resize: 'vertical' }}
                />
                {error && <span style={{ fontSize: 11.5, color: '#dc2626' }}>{error}</span>}
            </label>
        );
    }

    if (item.type === 'select') {
        return (
            <label style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                {labelEl}
                <select
                    value={local}
                    onChange={(e) => { setLocal(e.target.value); onCommit(e.target.value); }}
                    disabled={readonly || item.readonly}
                    style={base}
                >
                    <option value="">Select…</option>
                    {(item.options || []).map(opt => <option key={opt} value={opt}>{opt}</option>)}
                </select>
                {error && <span style={{ fontSize: 11.5, color: '#dc2626' }}>{error}</span>}
            </label>
        );
    }

    if (item.type === 'checkbox') {
        return (
            <label style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 12px', border: `1px solid ${p.border}`, borderRadius: 8, cursor: 'pointer', background: p.surface }}>
                <input
                    type="checkbox"
                    checked={Boolean(value)}
                    onChange={(e) => onCommit(e.target.checked)}
                    style={{ width: 16, height: 16, accentColor: '#2563eb' }}
                />
                <span style={{ fontSize: 13, color: p.textPrimary, fontWeight: 500 }}>
                    {item.label}{item.required && <span style={{ color: '#dc2626' }}> *</span>}
                </span>
            </label>
        );
    }

    return (
        <label style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {labelEl}
            <input
                type={item.type === 'number' ? 'number' : (item.type || 'text')}
                value={local}
                onChange={(e) => setLocal(e.target.value)}
                onBlur={commit}
                readOnly={readonly || item.readonly}
                placeholder={item.readonly ? '' : 'Enter value…'}
                style={base}
            />
            {error && <span style={{ fontSize: 11.5, color: '#dc2626' }}>{error}</span>}
        </label>
    );
};

/* ────────────────────────────────────────────────────────────
   Document upload card — used in the Documents step.
   ──────────────────────────────────────────────────────────── */
const DocCard = ({ item, file, onUpload }) => {
    const p = useP();
    const inputRef = useRef(null);
    const [progress, setProgress] = useState(null); // null | 0..100
    const [error, setError] = useState(null);

    const handleFile = useCallback(async (f) => {
        if (!f) return;
        setError(null);
        setProgress(0);
        const fakeProgress = setInterval(() => {
            setProgress((p) => (p == null ? 10 : Math.min(95, p + 8)));
        }, 120);
        try {
            // Same upload path the legacy wizard uses, so backend wiring is identical.
            const uploadResponse = await uploadService.uploadDocument(f, item.id);
            if (!uploadResponse?.success || !uploadResponse.data) {
                throw new Error(uploadResponse?.message || 'Upload failed');
            }
            const d = uploadResponse.data;
            const documentPayload = {
                name: d.originalName || f.name,
                fileName: d.originalName || f.name,
                url: d.downloadURL || d.url,
                type: d.mimeType || f.type,
                size: d.size ?? f.size,
                fileSize: d.size ?? f.size,
                filePath: d.filePath,
                storageFileName: d.fileName,
                uploadedAt: d.uploadedAt || new Date().toISOString(),
            };
            clearInterval(fakeProgress);
            setProgress(100);
            await onUpload(documentPayload);
            setTimeout(() => setProgress(null), 600);
        } catch (e) {
            clearInterval(fakeProgress);
            setError(e?.message || 'Upload failed');
            setProgress(null);
        }
    }, [item.id, onUpload]);

    const fmtSize = (n) => {
        if (!n) return '';
        if (n < 1024) return `${n} B`;
        if (n < 1024 * 1024) return `${Math.round(n / 1024)} KB`;
        return `${(n / (1024 * 1024)).toFixed(1)} MB`;
    };

    if (file && file.url && progress == null) {
        return (
            <div style={{ padding: 14, border: `1px solid ${p.docDoneBd}`, borderRadius: 10, background: p.docDoneBg, display: 'flex', gap: 12 }}>
                <div style={{ width: 38, height: 38, borderRadius: 8, background: p.docDoneBd, color: '#15803d', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                    <CheckCircle2 size={20} />
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 13, fontWeight: 700, color: p.textPrimary }}>{item.label}</div>
                    <div style={{ fontSize: 12, color: p.textMuted, marginTop: 2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {file.fileName || 'Uploaded'}{file.fileSize ? ` · ${fmtSize(file.fileSize)}` : ''}
                    </div>
                </div>
                <a href={file.url} target="_blank" rel="noreferrer" style={{ alignSelf: 'center', color: '#2563eb', fontSize: 12, fontWeight: 600, textDecoration: 'none' }}>View</a>
                <button onClick={() => inputRef.current?.click()} style={{ background: 'transparent', border: 'none', color: p.textMuted, cursor: 'pointer', padding: 4 }}>
                    <MoreHorizontal size={16} />
                </button>
                <input ref={inputRef} type="file" hidden onChange={(e) => handleFile(e.target.files?.[0])} accept=".pdf,.jpg,.jpeg,.png" />
            </div>
        );
    }

    if (progress != null) {
        return (
            <div style={{ padding: 14, border: `1px solid ${p.docPendBd}`, borderRadius: 10, background: p.docPendBg, display: 'flex', gap: 12, alignItems: 'center' }}>
                <div style={{ width: 38, height: 38, borderRadius: 8, background: p.docPendBd, color: '#2563eb', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                    <Upload size={18} />
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 13, fontWeight: 700, color: p.textPrimary }}>{item.label}</div>
                    <div style={{ fontSize: 11.5, color: p.textMuted, marginTop: 3, marginBottom: 5 }}>Uploading · {progress}%</div>
                    <div style={{ height: 4, background: p.progressTrack, borderRadius: 999, overflow: 'hidden' }}>
                        <div style={{ width: `${progress}%`, height: '100%', background: '#2563eb', borderRadius: 999, transition: 'width .2s' }} />
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div
            style={{ padding: 14, border: `1.5px dashed ${p.docEmptyBd}`, borderRadius: 10, background: p.docEmptyBg, display: 'flex', gap: 12, alignItems: 'center', cursor: 'pointer' }}
            onClick={() => inputRef.current?.click()}
            onDragOver={(e) => { e.preventDefault(); e.currentTarget.style.borderColor = '#2563eb'; }}
            onDragLeave={(e) => { e.currentTarget.style.borderColor = p.docEmptyBd; }}
            onDrop={(e) => { e.preventDefault(); e.currentTarget.style.borderColor = p.docEmptyBd; handleFile(e.dataTransfer.files?.[0]); }}
        >
            <div style={{ width: 38, height: 38, borderRadius: 8, background: p.surfaceMute, color: p.textMuted, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                <Upload size={18} />
            </div>
            <div style={{ flex: 1 }}>
                <div style={{ fontSize: 13, fontWeight: 700, color: p.textPrimary }}>{item.label}</div>
                <div style={{ fontSize: 12, color: p.textMuted, marginTop: 2 }}>
                    {error ? <span style={{ color: '#dc2626' }}>{error}</span> : 'Drag or click to upload'}
                </div>
            </div>
            <Btn variant="secondary" onClick={(e) => { e.stopPropagation(); inputRef.current?.click(); }}>Browse</Btn>
            <input ref={inputRef} type="file" hidden onChange={(e) => handleFile(e.target.files?.[0])} accept=".pdf,.jpg,.jpeg,.png" />
        </div>
    );
};

/* ────────────────────────────────────────────────────────────
   Main wizard.
   ──────────────────────────────────────────────────────────── */
const OnboardingWizardRedesign = () => {
    const { user } = useAuth();
    const themeCtx = useTheme();
    const isDark = themeCtx?.isDarkMode ?? (themeCtx?.theme === 'dark');
    const p = useMemo(() => buildPalette(!!isDark), [isDark]);
    const {
        onboardingData,
        updateFields,
        updateDocument,
        getCompletionPercentage,
        getItemStatus,
    } = useOnboarding();

    const employeeId = user?.id;
    const employeeRecord = useMemo(() => onboardingData?.[employeeId] || {}, [onboardingData, employeeId]);
    const savedFields = employeeRecord.fields || {};

    const [stepIdx, setStepIdx] = useState(0);
    const [lastSavedAt, setLastSavedAt] = useState(null);
    const [saving, setSaving] = useState(false);
    const [tick, setTick] = useState(0); // forces "saved Xs ago" re-render

    // Message-HR modal state — submits via the existing Vibe Voice-to-HR API.
    const [hrOpen, setHrOpen] = useState(false);
    const [hrText, setHrText] = useState('');
    const [hrAnon, setHrAnon] = useState(false);
    const [hrBusy, setHrBusy] = useState(false);
    const [hrError, setHrError] = useState(null);
    const [hrDone, setHrDone] = useState(false);

    const openHr = () => { setHrOpen(true); setHrText(''); setHrError(null); setHrDone(false); setHrAnon(false); };
    const closeHr = () => { if (!hrBusy) setHrOpen(false); };
    const submitHr = async () => {
        const msg = hrText.trim();
        if (!msg) { setHrError('Please write your message first.'); return; }
        setHrBusy(true);
        setHrError(null);
        try {
            await vibeService.submitSuggestion({
                content: msg,
                suggestion_category: 'onboarding',
                is_anonymous: hrAnon,
            });
            setHrDone(true);
            setTimeout(() => setHrOpen(false), 1600);
        } catch (e) {
            setHrError(e?.response?.data?.message || e?.message || 'Could not send. Please try again.');
        } finally {
            setHrBusy(false);
        }
    };

    useEffect(() => {
        const t = setInterval(() => setTick((x) => x + 1), 10_000);
        return () => clearInterval(t);
    }, []);

    // Merge what we have in context with prefill from the user/profile so the
    // form opens with everything we already know.
    const fields = useMemo(() => {
        const prefill = {
            full_name: user?.display_name || `${user?.first_name || ''} ${user?.last_name || ''}`.trim(),
            employee_id: user?.employee_code || '',
            date_of_birth: user?.date_of_birth || '',
            gender: user?.gender || '',
            personal_email: user?.personal_email || '',
            email_id: user?.email || '',
            official_email: user?.email || '',
            contact_number: user?.phone || '',
            current_address: user?.address || '',
            department_team: user?.department || '',
            designation: user?.designation || '',
            date_of_joining: user?.joining_date || user?.date_of_joining || '',
            reporting_manager: user?.managerName || '',
            employee_type: user?.employment_type || '',
            work_location: user?.work_location || '',
            blood_group: user?.blood_group || '',
        };
        return { ...prefill, ...savedFields };
    }, [user, savedFields]);

    const documents = employeeRecord.documents || {};

    /* ── Required-item visibility from the admin-configurable config ── */
    const isItemVisible = useCallback((item) => {
        const status = getItemStatus ? getItemStatus(item.id, item.required) : null;
        // Treat 'redundant'/'hidden' as not visible.
        return !(status === 'redundant' || status === 'hidden');
    }, [getItemStatus]);

    const sectionItems = useCallback((keys) => {
        const out = [];
        keys.forEach((sectionKey) => {
            const section = ONBOARDING_CHECKLIST[sectionKey];
            if (!section) return;
            section.items.forEach((item) => { if (isItemVisible(item)) out.push({ ...item, _section: sectionKey }); });
        });
        return out;
    }, [isItemVisible]);

    /* ── Wired writes ── */
    const commitField = useCallback(async (id, value) => {
        if (!employeeId) return;
        setSaving(true);
        try {
            await updateFields(employeeId, { [id]: value });
            setLastSavedAt(Date.now());
        } finally {
            setSaving(false);
        }
    }, [employeeId, updateFields]);

    const commitDocument = useCallback(async (id, doc) => {
        if (!employeeId) return;
        setSaving(true);
        try {
            await updateDocument(employeeId, id, doc);
            setLastSavedAt(Date.now());
        } finally {
            setSaving(false);
        }
    }, [employeeId, updateDocument]);

    /* ── Progress ── */
    const overallPct = useMemo(() => {
        if (!employeeId) return 0;
        return Math.round(getCompletionPercentage(employeeId) || 0);
    }, [employeeId, getCompletionPercentage, onboardingData]);  // eslint-disable-line react-hooks/exhaustive-deps

    /* ── Per-step counts (visible-only) ── */
    const stepStats = useMemo(() => {
        return STEPS.map((step) => {
            const items = sectionItems(step.sectionKeys);
            const total = items.length;
            const done = items.filter((it) => {
                if (it.type === 'file') return !!documents[it.id]?.url;
                const v = fields[it.id];
                if (it.type === 'checkbox') return Boolean(v);
                return v !== '' && v != null;
            }).length;
            return { total, done };
        });
    }, [sectionItems, fields, documents]);

    /* ── Footer "saved Xs ago" ── */
    const savedLabel = useMemo(() => {
        if (saving) return 'Saving…';
        if (!lastSavedAt) return 'Auto-saves as you type';
        const secs = Math.max(1, Math.round((Date.now() - lastSavedAt) / 1000));
        if (secs < 60) return `Auto-saved ${secs}s ago`;
        const mins = Math.round(secs / 60);
        return `Auto-saved ${mins}m ago`;
    }, [saving, lastSavedAt, tick]);

    /* ── Handlers ── */
    const next = () => setStepIdx((i) => Math.min(STEPS.length - 1, i + 1));
    const back = () => setStepIdx((i) => Math.max(0, i - 1));
    const submit = async () => {
        // The context auto-flips status when fields are submitted; we trigger
        // a final flush with whatever is on screen.
        if (!employeeId) return;
        setSaving(true);
        try {
            await updateFields(employeeId, fields);
            setLastSavedAt(Date.now());
        } finally {
            setSaving(false);
        }
    };

    const currentStep = STEPS[stepIdx];

    // ── Completed state ──────────────────────────────────────────
    // Once HR signs off (adminReviewed=true), the wizard is replaced
    // by a read-only "Onboarding complete" view. If HR later requests
    // changes (adminReviewed flips back to false), the wizard returns.
    const isApproved = !!employeeRecord.adminReviewed;
    if (isApproved) {
        return (
            <PaletteCtx.Provider value={p}>
                <CompletedView
                    p={p}
                    user={user}
                    record={employeeRecord}
                    fields={fields}
                    documents={documents}
                    openHr={openHr}
                    hrModal={{
                        hrOpen, hrText, hrAnon, hrBusy, hrError, hrDone,
                        setHrText, setHrAnon, submitHr, closeHr,
                    }}
                />
            </PaletteCtx.Provider>
        );
    }

    return (
        <PaletteCtx.Provider value={p}>
        <div style={{ minHeight: 'calc(100vh - 80px)', background: p.appBg, padding: 28, fontFamily: 'Inter, system-ui, sans-serif', color: p.textPrimary }}>
            {/* Page header */}
            <div style={{ maxWidth: 1280, margin: '0 auto', marginBottom: 18 }}>
                <div style={{ fontSize: 12.5, color: p.textMuted, fontWeight: 600, letterSpacing: 0.4, textTransform: 'uppercase' }}>Welcome to OakHR</div>
                <h1 style={{ margin: '4px 0 6px', fontSize: 26, fontWeight: 700, letterSpacing: -0.4, color: p.textPrimary }}>
                    Let's get you set up{user?.first_name ? `, ${user.first_name}` : ''}
                </h1>
                <p style={{ margin: 0, fontSize: 14, color: p.textSecondary }}>
                    {STEPS.length} quick steps. About 12 minutes. You can save anytime and come back later.
                </p>
            </div>

            <div style={{ maxWidth: 1280, margin: '0 auto', display: 'grid', gridTemplateColumns: '240px 1fr 280px', gap: 20, alignItems: 'start' }}>
                {/* LEFT: step rail */}
                <Card padding={16}>
                    <div style={{ marginBottom: 14 }}>
                        <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', marginBottom: 6 }}>
                            <span style={{ fontSize: 11.5, fontWeight: 700, color: p.textMuted, letterSpacing: 0.6, textTransform: 'uppercase' }}>Progress</span>
                            <span style={{ fontSize: 13, fontWeight: 700, color: '#60a5fa' }}>{overallPct}%</span>
                        </div>
                        <div style={{ height: 6, background: p.progressTrack, borderRadius: 999, overflow: 'hidden' }}>
                            <div style={{ width: `${overallPct}%`, height: '100%', background: 'linear-gradient(90deg, #2563eb, #60a5fa)', borderRadius: 999, transition: 'width .3s' }} />
                        </div>
                    </div>

                    <div style={{ display: 'flex', flexDirection: 'column', gap: 4, position: 'relative' }}>
                        <span style={{ position: 'absolute', left: 15, top: 14, bottom: 14, width: 2, background: p.progressTrack }} />
                        {STEPS.map((s, i) => {
                            const stat = stepStats[i] || { total: 0, done: 0 };
                            const isCurrent = i === stepIdx;
                            const isDone = i < stepIdx || (stat.total > 0 && stat.done >= stat.total);
                            const Icon = s.icon;
                            return (
                                <button
                                    key={s.id}
                                    onClick={() => setStepIdx(i)}
                                    style={{
                                        all: 'unset',
                                        display: 'flex', alignItems: 'center', gap: 11,
                                        padding: '8px 6px', borderRadius: 8,
                                        background: isCurrent ? (isDark ? 'rgba(37,99,235,0.15)' : '#eff6ff') : 'transparent',
                                        cursor: 'pointer', position: 'relative',
                                    }}
                                >
                                    <span style={{
                                        width: 30, height: 30, borderRadius: 999, flexShrink: 0,
                                        display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                                        background: isDone ? '#16a34a' : isCurrent ? '#2563eb' : p.surface,
                                        color: isDone || isCurrent ? 'white' : p.textMuted,
                                        border: isDone || isCurrent ? 'none' : `2px solid ${p.border}`,
                                        boxShadow: isCurrent ? (isDark ? '0 0 0 4px rgba(37,99,235,0.25)' : '0 0 0 4px #dbeafe') : 'none',
                                        zIndex: 1,
                                    }}>
                                        {isDone ? <Check size={15} strokeWidth={2.5} /> : <Icon size={16} />}
                                    </span>
                                    <div style={{ minWidth: 0 }}>
                                        <div style={{ fontSize: 13, fontWeight: isCurrent ? 700 : 600, color: isCurrent ? '#60a5fa' : isDone ? p.textPrimary : p.textMuted }}>{s.label}</div>
                                        <div style={{ fontSize: 11.5, color: p.textMuted, marginTop: 1 }}>
                                            {stat.total > 0 ? `${stat.done} of ${stat.total}` : s.blurb}
                                        </div>
                                    </div>
                                </button>
                            );
                        })}
                    </div>

                    <div style={{ marginTop: 14, padding: 12, background: p.autosaveBg, border: `1px solid ${p.autosaveBd}`, borderRadius: 8, fontSize: 12, color: p.autosaveFg, display: 'flex', gap: 8 }}>
                        <Sparkles size={14} style={{ color: '#ca8a04', flexShrink: 0, marginTop: 1 }} />
                        <div>
                            <div style={{ fontWeight: 700, marginBottom: 2 }}>Auto-saving as you type</div>
                            <div>Leave anytime — your progress is safe.</div>
                        </div>
                    </div>
                </Card>

                {/* CENTER: step content */}
                <Card padding={0} style={{ display: 'flex', flexDirection: 'column', overflow: 'hidden', minHeight: 540 }}>
                    <div style={{ flex: 1, padding: 28, overflow: 'auto' }}>
                        {currentStep.id === 'welcome' && (
                            <StepWelcome firstName={user?.first_name} steps={STEPS} />
                        )}
                        {currentStep.id === 'about' && (
                            <StepFields
                                title="About you"
                                subtitle="We pre-filled what we had. Double-check and add the missing pieces."
                                items={sectionItems(currentStep.sectionKeys)}
                                fields={fields}
                                commitField={commitField}
                                stat={stepStats[stepIdx]}
                                badgeTone="blue"
                            />
                        )}
                        {currentStep.id === 'documents' && (
                            <StepDocuments
                                items={sectionItems(currentStep.sectionKeys).filter((it) => it.type === 'file')}
                                documents={documents}
                                employeeId={employeeId}
                                onUpload={commitDocument}
                                stat={(() => {
                                    const items = sectionItems(currentStep.sectionKeys).filter((it) => it.type === 'file');
                                    const done = items.filter((it) => !!documents[it.id]?.url).length;
                                    return { total: items.length, done };
                                })()}
                            />
                        )}
                        {currentStep.id === 'banking' && (
                            <StepFields
                                title="Banking & Pay"
                                subtitle="For your monthly salary and reimbursements."
                                items={sectionItems(currentStep.sectionKeys)}
                                fields={fields}
                                commitField={commitField}
                                stat={stepStats[stepIdx]}
                                badgeTone="blue"
                                files={documents}
                                employeeId={employeeId}
                                onUpload={commitDocument}
                            />
                        )}
                        {currentStep.id === 'review' && (
                            <StepReview
                                fields={fields}
                                documents={documents}
                                stepStats={stepStats}
                                items={sectionItems(currentStep.sectionKeys)}
                                commitField={commitField}
                            />
                        )}
                    </div>

                    <div style={{
                        borderTop: `1px solid ${p.borderSoft}`,
                        padding: '14px 24px',
                        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                        background: p.surface2,
                    }}>
                        <Btn variant="ghost" onClick={back} disabled={stepIdx === 0} leftIcon={<ChevronLeft size={14} />}>Back</Btn>
                        <div style={{ fontSize: 12, color: p.textMuted, display: 'flex', alignItems: 'center', gap: 6 }}>
                            <Clock size={13} /> {savedLabel}
                        </div>
                        {stepIdx < STEPS.length - 1 ? (
                            <Btn variant="primary" onClick={next} rightIcon={<ChevronRight size={14} />}>Save & continue</Btn>
                        ) : (
                            <Btn variant="success" onClick={submit} rightIcon={<ChevronRight size={14} />}>Submit for review</Btn>
                        )}
                    </div>
                </Card>

                {/* RIGHT: helper rail */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                    <Card padding={16} style={{ background: 'linear-gradient(135deg, #0b0f1a 0%, #1e1b4b 100%)', color: 'white', border: 'none' }}>
                        <div style={{ fontSize: 11, fontWeight: 700, color: '#facc15', letterSpacing: 0.6, textTransform: 'uppercase' }}>Need help?</div>
                        <div style={{ fontSize: 13.5, fontWeight: 600, margin: '6px 0 10px', lineHeight: 1.35 }}>
                            Stuck on a step? HR usually replies within an hour.
                        </div>
                        <Btn
                            variant="gold"
                            leftIcon={<Mail size={12} />}
                            onClick={openHr}
                            style={{ padding: '6px 12px', fontSize: 12.5 }}
                        >
                            Message HR
                        </Btn>
                    </Card>
                </div>
            </div>

            {hrOpen && (
                <div
                    onClick={closeHr}
                    style={{
                        position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        zIndex: 1000, padding: 24,
                    }}
                >
                    <div
                        onClick={(e) => e.stopPropagation()}
                        style={{
                            background: p.surface, color: p.textPrimary,
                            border: `1px solid ${p.border}`, borderRadius: 12,
                            width: '100%', maxWidth: 480,
                            boxShadow: '0 24px 60px -12px rgba(0,0,0,0.45)',
                        }}
                    >
                        <div style={{ padding: '16px 18px', borderBottom: `1px solid ${p.borderSoft}`, display: 'flex', alignItems: 'center', gap: 10 }}>
                            <Mail size={16} style={{ color: '#facc15' }} />
                            <div style={{ flex: 1 }}>
                                <h3 style={{ margin: 0, fontSize: 15, fontWeight: 700, color: p.textPrimary }}>Message HR</h3>
                                <p style={{ margin: '2px 0 0', fontSize: 12, color: p.textMuted }}>
                                    Goes to the HR Voice-to-HR inbox. Usually a reply within an hour.
                                </p>
                            </div>
                            <button onClick={closeHr} style={{ background: 'transparent', border: 'none', color: p.textMuted, cursor: 'pointer', padding: 4 }}>
                                <X size={14} />
                            </button>
                        </div>
                        <div style={{ padding: 18 }}>
                            {hrDone ? (
                                <div style={{
                                    padding: 14, borderRadius: 10,
                                    background: p.successBg, border: `1px solid ${p.successBd}`,
                                    color: p.successFg, fontSize: 13, display: 'flex', gap: 10, alignItems: 'center',
                                }}>
                                    <CheckCircle2 size={18} />
                                    <div>
                                        <b>Sent.</b> HR will see your message and reply to your work email.
                                    </div>
                                </div>
                            ) : (
                                <>
                                    <label style={{ fontSize: 12.5, fontWeight: 600, color: p.textSecondary, display: 'block', marginBottom: 6 }}>
                                        What's blocking you?
                                    </label>
                                    <textarea
                                        value={hrText}
                                        onChange={(e) => setHrText(e.target.value)}
                                        placeholder="e.g. I can't find my offer letter to upload — could you resend it?"
                                        rows={5}
                                        autoFocus
                                        style={{
                                            width: '100%',
                                            background: p.inputBg, color: p.inputText,
                                            border: `1px solid ${p.inputBorder}`, borderRadius: 8,
                                            padding: '10px 12px', fontSize: 14, fontFamily: 'inherit',
                                            resize: 'vertical',
                                        }}
                                    />
                                    <label style={{ marginTop: 10, display: 'flex', alignItems: 'center', gap: 8, fontSize: 12.5, color: p.textSecondary, cursor: 'pointer' }}>
                                        <input
                                            type="checkbox"
                                            checked={hrAnon}
                                            onChange={(e) => setHrAnon(e.target.checked)}
                                            style={{ width: 14, height: 14, accentColor: '#2563eb' }}
                                        />
                                        Send anonymously (HR won't see your name)
                                    </label>
                                    {hrError && (
                                        <div style={{ marginTop: 8, fontSize: 12, color: '#fca5a5' }}>{hrError}</div>
                                    )}
                                </>
                            )}
                        </div>
                        {!hrDone && (
                            <div style={{ padding: '12px 18px', borderTop: `1px solid ${p.borderSoft}`, display: 'flex', justifyContent: 'flex-end', gap: 8, background: p.surface2 }}>
                                <Btn variant="ghost" onClick={closeHr} disabled={hrBusy}>Cancel</Btn>
                                <Btn variant="gold" onClick={submitHr} disabled={hrBusy} leftIcon={<Mail size={12} />}>
                                    {hrBusy ? 'Sending…' : 'Send to HR'}
                                </Btn>
                            </div>
                        )}
                    </div>
                </div>
            )}
        </div>
        </PaletteCtx.Provider>
    );
};

/* ────────────────────────────────────────────────────────────
   Step bodies
   ──────────────────────────────────────────────────────────── */
/* ────────────────────────────────────────────────────────────
   Completed view — shown to employees whose onboarding has been
   approved by HR. Read-only summary; the wizard is hidden until
   HR requests changes (which flips adminReviewed back to false).
   ──────────────────────────────────────────────────────────── */
const CompletedView = ({ p, user, record, fields, documents, openHr, hrModal }) => {
    const approvedDate = record.completedAt || record.reviewedAt;
    const approvedBy = record.reviewedBy || 'HR';
    const fmtDate = (d) => d ? new Date(d).toLocaleDateString('en-IN', { day: 'numeric', month: 'long', year: 'numeric' }) : '—';

    const fmtSize = (n) => {
        if (!n) return '';
        if (n < 1024) return `${n} B`;
        if (n < 1024 * 1024) return `${Math.round(n / 1024)} KB`;
        return `${(n / (1024 * 1024)).toFixed(1)} MB`;
    };

    // Build the read-only summary off the same checklist groupings the wizard used.
    const sections = [
        { key: 'basicInfo',           label: 'About you', icon: User },
        { key: 'identityDocuments',   label: 'Identity documents', icon: FileText },
        { key: 'educationalDocuments', label: 'Educational documents', icon: FileText },
        { key: 'payrollBanking',      label: 'Banking & pay', icon: Landmark },
    ];

    return (
        <div style={{ minHeight: 'calc(100vh - 80px)', background: p.appBg, padding: 28, fontFamily: 'Inter, system-ui, sans-serif', color: p.textPrimary }}>
            <div style={{ maxWidth: 880, margin: '0 auto' }}>
                {/* Hero card */}
                <Card padding={0} style={{ overflow: 'hidden', marginBottom: 18 }}>
                    <div style={{
                        background: 'linear-gradient(135deg, #14532d 0%, #16a34a 60%, #22c55e 130%)',
                        padding: '32px 28px', color: 'white', position: 'relative', overflow: 'hidden',
                    }}>
                        <div style={{ position: 'absolute', right: -30, top: -30, width: 180, height: 180, borderRadius: 999, background: 'rgba(255,255,255,0.08)' }} />
                        <div style={{ position: 'absolute', right: 40, bottom: -50, width: 100, height: 100, borderRadius: 999, background: 'rgba(255,255,255,0.10)' }} />
                        <div style={{ position: 'relative', display: 'flex', alignItems: 'center', gap: 18 }}>
                            <div style={{
                                width: 56, height: 56, borderRadius: 14,
                                background: 'rgba(255,255,255,0.18)',
                                display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                                flexShrink: 0,
                                boxShadow: 'inset 0 0 0 1.5px rgba(255,255,255,0.25)',
                            }}>
                                <CheckCircle2 size={32} strokeWidth={2.5} />
                            </div>
                            <div style={{ flex: 1, minWidth: 0 }}>
                                <div style={{ fontSize: 11, fontWeight: 700, color: '#bbf7d0', letterSpacing: 0.8, textTransform: 'uppercase' }}>
                                    All done · {fmtDate(approvedDate)}
                                </div>
                                <h1 style={{ fontSize: 28, fontWeight: 700, margin: '6px 0 4px', letterSpacing: -0.5 }}>
                                    You're all onboarded{user?.first_name ? `, ${user.first_name}` : ''} 🎉
                                </h1>
                                <p style={{ margin: 0, fontSize: 13.5, color: 'rgba(255,255,255,0.88)', lineHeight: 1.45 }}>
                                    HR signed off your submission{approvedBy ? ` (${approvedBy})` : ''}.
                                    Your records are filed and your team has been notified.
                                </p>
                            </div>
                        </div>
                    </div>
                </Card>

                {/* Quick info strip */}
                <Card padding={20} style={{ marginBottom: 18 }}>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 16 }}>
                        <InfoCell p={p} label="Approved on" value={fmtDate(approvedDate)} />
                        <InfoCell p={p} label="Approved by" value={approvedBy} />
                        <InfoCell p={p} label="Status" value="Active employee" valueColor="#22c55e" />
                    </div>
                </Card>

                {/* Your submission (read-only) */}
                <Card padding={0} style={{ overflow: 'hidden', marginBottom: 18 }}>
                    <div style={{ padding: '14px 20px', borderBottom: `1px solid ${p.borderSoft}`, background: p.surface2 }}>
                        <div style={{ fontSize: 11, fontWeight: 700, color: p.textMuted, letterSpacing: 0.4, textTransform: 'uppercase' }}>
                            Your submission
                        </div>
                        <div style={{ fontSize: 13.5, fontWeight: 600, color: p.textPrimary, marginTop: 2 }}>
                            Saved details and uploaded documents
                        </div>
                    </div>
                    {sections.map(({ key, label, icon: Icon }) => {
                        const section = ONBOARDING_CHECKLIST[key];
                        if (!section) return null;
                        const dataItems = section.items.filter((it) => it.type !== 'file' && it.type !== 'checkbox');
                        const fileItems = section.items.filter((it) => it.type === 'file');
                        const populatedData = dataItems.filter((it) => fields[it.id] != null && fields[it.id] !== '');
                        const populatedFiles = fileItems.filter((it) => documents[it.id]?.url);
                        if (populatedData.length === 0 && populatedFiles.length === 0) return null;
                        return (
                            <div key={key} style={{ padding: '16px 20px', borderTop: `1px solid ${p.borderSoft}` }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
                                    <span style={{ width: 28, height: 28, borderRadius: 7, background: p.pillBlueBg, color: '#60a5fa', display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>
                                        <Icon size={14} />
                                    </span>
                                    <div style={{ fontSize: 13.5, fontWeight: 700, color: p.textPrimary }}>{label}</div>
                                </div>
                                {populatedData.length > 0 && (
                                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: populatedFiles.length ? 12 : 0 }}>
                                        {populatedData.map((it) => (
                                            <div key={it.id} style={{ fontSize: 12.5, padding: '6px 0' }}>
                                                <div style={{ color: p.textMuted, fontSize: 11.5 }}>{it.label}</div>
                                                <div style={{ color: p.textPrimary, fontWeight: 500, marginTop: 2, wordBreak: 'break-word' }}>
                                                    {String(fields[it.id])}
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                )}
                                {populatedFiles.length > 0 && (
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                                        {populatedFiles.map((it) => {
                                            const f = documents[it.id];
                                            return (
                                                <div key={it.id} style={{
                                                    display: 'flex', alignItems: 'center', gap: 10,
                                                    padding: '8px 10px',
                                                    border: `1px solid ${p.docDoneBd}`, borderRadius: 8,
                                                    background: p.docDoneBg,
                                                }}>
                                                    <div style={{ width: 28, height: 28, borderRadius: 6, background: p.docDoneBd, color: '#15803d', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                                                        <FileText size={14} />
                                                    </div>
                                                    <div style={{ flex: 1, minWidth: 0 }}>
                                                        <div style={{ fontSize: 12.5, fontWeight: 600, color: p.textPrimary }}>{it.label}</div>
                                                        <div style={{ fontSize: 11, color: p.textMuted, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                                            {f.fileName || f.name || 'Uploaded'}{f.fileSize || f.size ? ` · ${fmtSize(f.fileSize || f.size)}` : ''}
                                                        </div>
                                                    </div>
                                                    <a href={f.url} target="_blank" rel="noreferrer" style={{ color: '#60a5fa', fontSize: 12, fontWeight: 600, textDecoration: 'none' }}>
                                                        View
                                                    </a>
                                                </div>
                                            );
                                        })}
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </Card>

                {/* Need-a-change footer */}
                <Card padding={20}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
                        <span style={{ width: 36, height: 36, borderRadius: 8, background: p.pillAmberBg, color: p.pillAmberFg, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                            <Mail size={18} />
                        </span>
                        <div style={{ flex: 1 }}>
                            <div style={{ fontSize: 13.5, fontWeight: 700, color: p.textPrimary }}>
                                Something needs to change?
                            </div>
                            <div style={{ fontSize: 12.5, color: p.textMuted, marginTop: 2 }}>
                                Message HR. They'll review and reopen your onboarding if any updates are needed.
                            </div>
                        </div>
                        <Btn variant="gold" leftIcon={<Mail size={12} />} onClick={openHr} style={{ padding: '8px 14px' }}>
                            Message HR
                        </Btn>
                    </div>
                </Card>
            </div>

            {/* Message-HR modal (reuses the same Vibe Voice-to-HR submitter) */}
            {hrModal.hrOpen && (
                <div
                    onClick={hrModal.closeHr}
                    style={{
                        position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        zIndex: 1000, padding: 24,
                    }}
                >
                    <div
                        onClick={(e) => e.stopPropagation()}
                        style={{
                            background: p.surface, color: p.textPrimary,
                            border: `1px solid ${p.border}`, borderRadius: 12,
                            width: '100%', maxWidth: 480,
                            boxShadow: '0 24px 60px -12px rgba(0,0,0,0.45)',
                        }}
                    >
                        <div style={{ padding: '16px 18px', borderBottom: `1px solid ${p.borderSoft}`, display: 'flex', alignItems: 'center', gap: 10 }}>
                            <Mail size={16} style={{ color: '#facc15' }} />
                            <div style={{ flex: 1 }}>
                                <h3 style={{ margin: 0, fontSize: 15, fontWeight: 700, color: p.textPrimary }}>Message HR</h3>
                                <p style={{ margin: '2px 0 0', fontSize: 12, color: p.textMuted }}>
                                    Goes to the HR Voice-to-HR inbox. Usually a reply within an hour.
                                </p>
                            </div>
                            <button onClick={hrModal.closeHr} style={{ background: 'transparent', border: 'none', color: p.textMuted, cursor: 'pointer', padding: 4 }}>
                                <X size={14} />
                            </button>
                        </div>
                        <div style={{ padding: 18 }}>
                            {hrModal.hrDone ? (
                                <div style={{
                                    padding: 14, borderRadius: 10,
                                    background: p.successBg, border: `1px solid ${p.successBd}`,
                                    color: p.successFg, fontSize: 13, display: 'flex', gap: 10, alignItems: 'center',
                                }}>
                                    <CheckCircle2 size={18} />
                                    <div><b>Sent.</b> HR will see your message and reply to your work email.</div>
                                </div>
                            ) : (
                                <>
                                    <label style={{ fontSize: 12.5, fontWeight: 600, color: p.textSecondary, display: 'block', marginBottom: 6 }}>
                                        What would you like to update?
                                    </label>
                                    <textarea
                                        value={hrModal.hrText}
                                        onChange={(e) => hrModal.setHrText(e.target.value)}
                                        placeholder="e.g. My address has changed since onboarding — please update."
                                        rows={5}
                                        autoFocus
                                        style={{
                                            width: '100%',
                                            background: p.inputBg, color: p.inputText,
                                            border: `1px solid ${p.inputBorder}`, borderRadius: 8,
                                            padding: '10px 12px', fontSize: 14, fontFamily: 'inherit', resize: 'vertical',
                                        }}
                                    />
                                    <label style={{ marginTop: 10, display: 'flex', alignItems: 'center', gap: 8, fontSize: 12.5, color: p.textSecondary, cursor: 'pointer' }}>
                                        <input
                                            type="checkbox"
                                            checked={hrModal.hrAnon}
                                            onChange={(e) => hrModal.setHrAnon(e.target.checked)}
                                            style={{ width: 14, height: 14, accentColor: '#2563eb' }}
                                        />
                                        Send anonymously (HR won't see your name)
                                    </label>
                                    {hrModal.hrError && (
                                        <div style={{ marginTop: 8, fontSize: 12, color: '#fca5a5' }}>{hrModal.hrError}</div>
                                    )}
                                </>
                            )}
                        </div>
                        {!hrModal.hrDone && (
                            <div style={{ padding: '12px 18px', borderTop: `1px solid ${p.borderSoft}`, display: 'flex', justifyContent: 'flex-end', gap: 8, background: p.surface2 }}>
                                <Btn variant="ghost" onClick={hrModal.closeHr} disabled={hrModal.hrBusy}>Cancel</Btn>
                                <Btn variant="gold" onClick={hrModal.submitHr} disabled={hrModal.hrBusy} leftIcon={<Mail size={12} />}>
                                    {hrModal.hrBusy ? 'Sending…' : 'Send to HR'}
                                </Btn>
                            </div>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

const InfoCell = ({ p, label, value, valueColor }) => (
    <div>
        <div style={{ fontSize: 11, fontWeight: 700, color: p.textMuted, letterSpacing: 0.5, textTransform: 'uppercase' }}>
            {label}
        </div>
        <div style={{ fontSize: 14, fontWeight: 600, color: valueColor || p.textPrimary, marginTop: 4, wordBreak: 'break-word' }}>
            {value}
        </div>
    </div>
);

const StepWelcome = ({ firstName, steps }) => {
    const p = useP();
    return (
    <div>
        <div style={{
            background: 'linear-gradient(135deg, #1e3a8a 0%, #2563eb 60%, #eab308 130%)',
            borderRadius: 12, padding: '28px 26px', color: 'white', marginBottom: 22, position: 'relative', overflow: 'hidden',
        }}>
            <div style={{ position: 'absolute', right: -30, top: -30, width: 160, height: 160, borderRadius: 999, background: 'rgba(255,255,255,0.08)' }} />
            <div style={{ position: 'absolute', right: 30, bottom: -50, width: 100, height: 100, borderRadius: 999, background: 'rgba(234,179,8,0.25)' }} />
            <div style={{ position: 'relative' }}>
                <div style={{ fontSize: 12, fontWeight: 700, color: '#facc15', letterSpacing: 0.8, textTransform: 'uppercase' }}>Day 1</div>
                <h2 style={{ fontSize: 28, margin: '8px 0 6px', letterSpacing: -0.5, lineHeight: 1.15 }}>Welcome to the team{firstName ? `, ${firstName}` : ''} 🎉</h2>
                <p style={{ margin: 0, fontSize: 14, color: 'rgba(255,255,255,0.85)', maxWidth: 480 }}>
                    We can't wait to work with you. This setup takes about 12 minutes — and we'll do most of the heavy lifting in the background.
                </p>
            </div>
        </div>

        <h3 style={{ fontSize: 16, fontWeight: 700, margin: '0 0 12px', color: p.textPrimary }}>What to expect</h3>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            {[
                ['Personal info', 'Verify the basics we already have on file.', User, '#2563eb'],
                ['Documents', 'Aadhaar, PAN, Education, Resume — drag in.', FileText, '#16a34a'],
                ['Bank setup', 'For salary direct deposit & reimbursements.', Landmark, '#7e22ce'],
                ['Quick review', "HR approves, you're onboarded.", CheckCircle2, '#ca8a04'],
            ].map(([title, body, Icon, color], i) => (
                <div key={i} style={{ display: 'flex', gap: 12, padding: 14, border: `1px solid ${p.borderSoft}`, borderRadius: 10, background: p.surface2 }}>
                    <div style={{ width: 36, height: 36, borderRadius: 8, background: `${color}25`, color, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                        <Icon size={18} />
                    </div>
                    <div>
                        <div style={{ fontSize: 13.5, fontWeight: 700, color: p.textPrimary }}>{title}</div>
                        <div style={{ fontSize: 12.5, color: p.textMuted, marginTop: 2, lineHeight: 1.45 }}>{body}</div>
                    </div>
                </div>
            ))}
        </div>
    </div>
    );
};

const StepFields = ({ title, subtitle, items, fields, commitField, stat, badgeTone, files, employeeId, onUpload }) => {
    const p = useP();
    const fileItems = items.filter((it) => it.type === 'file');
    const dataItems = items.filter((it) => it.type !== 'file');
    return (
        <div>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 4 }}>
                <h2 style={{ fontSize: 20, fontWeight: 700, margin: 0, letterSpacing: -0.2, color: p.textPrimary }}>{title}</h2>
                {stat && <Pill tone={stat.done >= stat.total && stat.total > 0 ? 'green' : badgeTone || 'blue'}><Check size={11} strokeWidth={2.5} /> {stat.done} of {stat.total} filled</Pill>}
            </div>
            <p style={{ margin: '4px 0 22px', fontSize: 13, color: p.textMuted }}>{subtitle}</p>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
                {dataItems.map((it) => (
                    <Field
                        key={it.id}
                        item={it}
                        value={fields[it.id]}
                        onCommit={(v) => commitField(it.id, v)}
                    />
                ))}
            </div>

            {fileItems.length > 0 && (
                <>
                    <div style={{ marginTop: 22, marginBottom: 10, fontSize: 12.5, fontWeight: 700, color: p.textSecondary, textTransform: 'uppercase', letterSpacing: 0.4 }}>
                        Supporting documents
                    </div>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                        {fileItems.map((it) => (
                            <DocCard
                                key={it.id}
                                item={it}
                                file={files?.[it.id]}
                                employeeId={employeeId}
                                onUpload={(d) => onUpload(it.id, d)}
                            />
                        ))}
                    </div>
                </>
            )}

            <div style={{ marginTop: 18, padding: 12, background: p.privacyBg, border: `1px solid ${p.privacyBd}`, borderRadius: 8, display: 'flex', gap: 10, fontSize: 12.5, color: p.privacyFg }}>
                <Lock size={14} style={{ flexShrink: 0, marginTop: 1 }} />
                <div>
                    <b>Your data is private.</b> Only HR and your reporting manager can see your personal info. We never share with third parties.
                </div>
            </div>
        </div>
    );
};

const StepDocuments = ({ items, documents, employeeId, onUpload, stat }) => {
    const p = useP();
    return (
        <div>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 4 }}>
                <h2 style={{ fontSize: 20, fontWeight: 700, margin: 0, letterSpacing: -0.2, color: p.textPrimary }}>Documents</h2>
                <Pill tone={stat.done >= stat.total && stat.total > 0 ? 'green' : 'amber'}><Upload size={11} /> {stat.done} of {stat.total} uploaded</Pill>
            </div>
            <p style={{ margin: '4px 0 18px', fontSize: 13, color: p.textMuted }}>Drag files in or browse. PDF, JPG, or PNG — up to 10 MB each.</p>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                {items.map((it) => (
                    <DocCard
                        key={it.id}
                        item={it}
                        file={documents?.[it.id]}
                        employeeId={employeeId}
                        onUpload={(d) => onUpload(it.id, d)}
                    />
                ))}
            </div>
        </div>
    );
};

const StepReview = ({ fields, documents, stepStats, items, commitField }) => {
    const p = useP();
    const sections = [
        { label: 'About you',  stat: stepStats[1], icon: User,        color: '#2563eb' },
        { label: 'Documents',  stat: stepStats[2], icon: FileText,    color: '#16a34a' },
        { label: 'Banking',    stat: stepStats[3], icon: Landmark,    color: '#7e22ce' },
    ];

    const ackItems = items.filter((it) => it.type === 'checkbox');
    const allDone = sections.every(s => (s.stat?.total || 0) === 0 || s.stat.done >= s.stat.total);

    return (
        <div>
            <h2 style={{ fontSize: 20, fontWeight: 700, margin: 0, letterSpacing: -0.2, color: p.textPrimary }}>Review &amp; submit</h2>
            <p style={{ margin: '4px 0 18px', fontSize: 13, color: p.textMuted }}>One last look. After you submit, HR reviews and signs you off — usually within 24 hours.</p>

            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                {sections.map((s, i) => {
                    const Icon = s.icon;
                    const total = s.stat?.total ?? 0;
                    const done = s.stat?.done ?? 0;
                    const complete = total > 0 && done >= total;
                    return (
                        <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: 14, border: `1px solid ${p.borderSoft}`, borderRadius: 10, background: complete ? p.surface2 : p.surface }}>
                            <div style={{ width: 36, height: 36, borderRadius: 8, background: `${s.color}28`, color: s.color, display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>
                                <Icon size={18} />
                            </div>
                            <div style={{ flex: 1 }}>
                                <div style={{ fontSize: 13.5, fontWeight: 700, color: p.textPrimary }}>{s.label}</div>
                                <div style={{ fontSize: 12, color: p.textMuted, marginTop: 2 }}>
                                    {total === 0 ? 'No required fields' : `${done} of ${total} ${complete ? 'complete' : 'filled'}`}
                                </div>
                            </div>
                            <Pill tone={complete ? 'green' : 'amber'}>{complete ? 'Ready' : 'Needs review'}</Pill>
                        </div>
                    );
                })}
            </div>

            {ackItems.length > 0 && (
                <>
                    <div style={{ marginTop: 22, marginBottom: 10, fontSize: 12.5, fontWeight: 700, color: p.textSecondary, textTransform: 'uppercase', letterSpacing: 0.4 }}>
                        Acknowledgements
                    </div>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: 8 }}>
                        {ackItems.map((it) => (
                            <Field key={it.id} item={it} value={fields[it.id]} onCommit={(v) => commitField(it.id, v)} />
                        ))}
                    </div>
                </>
            )}

            <div style={{ marginTop: 16, padding: 14, background: allDone ? p.successBg : p.warnBg, border: `1px solid ${allDone ? p.successBd : p.warnBd}`, borderRadius: 10, display: 'flex', gap: 11 }}>
                <Sparkles size={18} style={{ color: allDone ? '#16a34a' : '#ca8a04', flexShrink: 0, marginTop: 1 }} />
                <div style={{ fontSize: 13, color: allDone ? p.successFg : p.warnFg, lineHeight: 1.5 }}>
                    {allDone ? (
                        <><b>You're all set.</b> Once you submit, we'll send your welcome email, add you to the team checklist, and notify your manager.</>
                    ) : (
                        <><b>A few items still need attention.</b> Jump back to any step from the rail on the left to finish up.</>
                    )}
                </div>
            </div>
        </div>
    );
};

export default OnboardingWizardRedesign;
